/* istanbul ignore file */
import React, { useEffect } from 'react';
import type { PropsWithChildren } from 'react';
import Axios, { AxiosError } from 'axios';
import { AUTH_REFRESH_TOKEN, useAuthToken } from '../Auth';
import { useRouter } from 'next/router';
import { AuthLoginDto } from '../../generated/nabisNYWebAPI.schemas';
import { auth } from '../link';
import { useMe } from '../User';

export const RefreshProvider = ({ children }: PropsWithChildren) => {
  const router = useRouter();
  const { setToken, setRefreshToken } = useAuthToken();
  const { clearUser } = useMe();

  useEffect(() => {
    Axios.interceptors.response.use(
      (r) => r,
      async (err: AxiosError) => {
        // We only want to run the promise refresh token re-routing if it's a failed request
        // to our API domain with a 401 error
        if (!err.config?.baseURL?.includes(Axios.defaults.baseURL ?? '') || err.response?.status !== 401) {
          return Promise.reject(err);
        }

        const unparsed = localStorage.getItem(AUTH_REFRESH_TOKEN);
        if (!unparsed) {
          const isOnLoginPage = router.pathname === auth.login;
          if (!isOnLoginPage) {
            await router.push(auth.login);
          }
          return Promise.resolve();
        }
        const refreshToken = JSON.parse(unparsed) as string;

        // If we have a refresh token, attempt to refresh our access token using the refresh token
        try {
          const refreshTokenAxios = Axios.create({
            baseURL: process.env.NEXT_PUBLIC_API_DOMAIN,
            headers: { Authorization: `Bearer ${refreshToken}` },
          });

          const res: { data: AuthLoginDto } = await refreshTokenAxios.get('/api/v1/auth/refresh');
          const newAccessToken = res.data.token;
          // With the newly refreshed access token, we can set the token back to local storage
          setToken(newAccessToken);

          // Once we have everything set back up, we attempt to make the previous request again
          if (err.config) {
            err.config.headers.Authorization = `Bearer ${newAccessToken}`;
            return Axios(err.config);
          }
        } catch (e) {
          // If the re-attempt fails, then we want to redirect to login and reset the tokens
          clearUser();
          setToken(null);
          setRefreshToken(null);

          const isOnLoginPage = router.pathname === auth.login;
          if (!isOnLoginPage) {
            await router.push(auth.login);
          }
          return Promise.resolve();
        }
      },
    );
  }, [router, setToken, setRefreshToken, clearUser]);

  return <>{children}</>;
};
